import { useEffect, useMemo, useState } from "react";
import ListingCard from "../components/ListingCard";
import ShopProductFilters from "../components/ShopProductFilters";
import TrustBadges from "../components/TrustBadges";
import { defaultShopProductFilters, filterShopProducts, shopProductCategories } from "../utils/shopProductFilters";

function SellerProfilePage({
  error,
  favoriteActionListingId,
  isLoading,
  onBack,
  onOpenListing,
  onToggleFavorite,
  profile,
}) {
  const [showReviews, setShowReviews] = useState(false);
  const [productFilters, setProductFilters] = useState(defaultShopProductFilters);

  useEffect(() => {
    setShowReviews(false);
    setProductFilters(defaultShopProductFilters);
  }, [profile?.id]);

  const shopListings = useMemo(() => profile?.active_listings || [], [profile?.active_listings]);
  const filteredShopListings = useMemo(
    () => filterShopProducts(shopListings, productFilters),
    [shopListings, productFilters]
  );
  const shopCategories = useMemo(() => shopProductCategories(shopListings), [shopListings]);

  if (isLoading) {
    return (
      <section className="main-panel">
        <div className="empty-results">Henter sælgerprofil...</div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="main-panel">
        <div className="page-heading">
          <div>
            <span className="mini-label">Sælgerprofil</span>
            <h1>Profilen kunne ikke hentes</h1>
            <p>{error}</p>
          </div>
          <button className="secondary-action page-action" onClick={onBack} type="button">
            Tilbage
          </button>
        </div>
      </section>
    );
  }

  if (!profile) {
    return (
      <section className="main-panel">
        <div className="empty-results">
          <strong>Ingen sælger valgt</strong>
          <p>Gå tilbage og vælg en annonce igen.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="main-panel">
      <div className="profile-hero seller-profile-hero" style={profile.role === "shop_owner" ? { borderTop: `6px solid ${profile.shop_color}` } : undefined}>
        {profile.shop_logo_url ? <img className="seller-shop-logo" src={profile.shop_logo_url} alt={`${profile.shop_name || profile.display_name} logo`} /> : <div className="profile-avatar">{initials(profile.display_name)}</div>}
        <div>
          <span className="mini-label">{profile.role === "shop_owner" ? "Butik" : profile.seller_verified ? "Verificeret sælger" : "Sælger"}</span>
          <h1>{profile.shop_name || profile.display_name}</h1>
          <p>
            {profile.location || "Lokation ikke angivet"}
            {profile.role !== "shop_owner" ? ` | ${ratingText(profile)}` : ""}
          </p>
          {profile.role !== "shop_owner" && (
            <TrustBadges
              completedSalesCount={profile.completed_sales_count}
              ratingAverage={profile.rating_average}
              ratingCount={profile.rating_count}
              verified={profile.seller_verified}
            />
          )}
        </div>
        <button className="secondary-action" onClick={onBack} type="button">
          Tilbage
        </button>
      </div>

      {profile.role !== "shop_owner" && <div className="seller-profile-grid">
        <article className="profile-card">
          <span className="mini-label">Rating</span>
          <div className="profile-rating">
            {profile.rating_count ? profile.rating_average.toFixed(1) : "-"}
          </div>
          <p>{ratingText(profile)}</p>
        </article>

        <article className="profile-card">
          <span className="mini-label">Sælgerstatus</span>
          <div className="profile-stats">
            <span>Solgt</span>
            <strong>{profile.completed_sales_count}</strong>
            <span>Købt</span>
            <strong>{profile.completed_purchases_count}</strong>
            <span>Aktive annoncer</span>
            <strong>{profile.active_listing_count}</strong>
            <span>Verificering</span>
            <strong>{profile.seller_verified ? "Ja" : "Ikke endnu"}</strong>
          </div>
        </article>
      </div>}

      <div className="section-toolbar">
        <div>
          <span className="mini-label">{profile.role === "shop_owner" ? "Butikkens varer" : "Annoncer"}</span>
          <h2>Aktive annoncer fra {profile.shop_name || profile.display_name}</h2>
        </div>
      </div>

      {profile.role === "shop_owner" && profile.active_listings.length > 0 && (
        <ShopProductFilters
          categories={shopCategories}
          filters={productFilters}
          onChange={setProductFilters}
          onReset={() => setProductFilters(defaultShopProductFilters)}
          resultCount={filteredShopListings.length}
        />
      )}

      {profile.active_listings.length === 0 ? (
        <div className="empty-results">
          <strong>Ingen aktive annoncer</strong>
          <p>{profile.role === "shop_owner" ? "Butikken har ikke nogen varer til salg lige nu." : "Sælgeren har ikke nogen aktive annoncer lige nu."}</p>
        </div>
      ) : profile.role === "shop_owner" && filteredShopListings.length === 0 ? (
        <div className="empty-results"><strong>Ingen varer fundet</strong><p>Prøv at ændre din søgning eller nulstille filtrene.</p></div>
      ) : (
        <div className="listing-grid">
          {(profile.role === "shop_owner" ? filteredShopListings : profile.active_listings).map((listing) => (
            <ListingCard
              favoriteActionListingId={favoriteActionListingId}
              key={listing.id}
              listing={listing}
              onOpen={onOpenListing}
              onToggleFavorite={onToggleFavorite}
            />
          ))}
        </div>
      )}

      <div className="section-toolbar">
        <div>
          <span className="mini-label">Anmeldelser</span>
          <h2>{profile.reviews?.length || 0} anmeldelser</h2>
        </div>
        <button className="secondary-action" onClick={() => setShowReviews((visible) => !visible)} type="button">
          {showReviews ? "Skjul anmeldelser" : "Vis anmeldelser"}
        </button>
      </div>

      {showReviews && (profile.reviews?.length ? (
        <div className="review-list">
          {profile.reviews.map((review) => (
            <article className="profile-card review-card" key={review.id}>
              <div className="review-card-header">
                <strong>{review.reviewer_display_name}</strong>
                <span>{`${review.rating}/5`}</span>
              </div>
              <p>{review.comment || "Ingen kommentar."}</p>
            </article>
          ))}
        </div>
      ) : (
        <div className="empty-results">
          <strong>Ingen anmeldelser endnu</strong>
          <p>{profile.role === "shop_owner" ? "Butikken har ikke modtaget anmeldelser endnu." : "Sælgeren har ikke modtaget offentlige vurderinger endnu."}</p>
        </div>
      ))}
    </section>
  );
}

function initials(name) {
  return (name || "S")
    .split(" ")
    .map((part) => part.trim()[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function ratingText(profile) {
  if (!profile.rating_count) {
    return "Ingen rating endnu";
  }

  return `${profile.rating_average.toFixed(1)} rating baseret paa ${profile.rating_count} handler`;
}

export default SellerProfilePage;
